import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Package, Search, ArrowUp, ArrowDown, FileSpreadsheet, FileText } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value);
};

const TIPOS = {
  MA: { label: 'Materiales', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  SE: { label: 'Servicios', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  GA: { label: 'Gtos. Grales.', cls: 'bg-purple-100 text-purple-700 border-purple-300' },
};

const shortName = (name, max = 20) => {
  const n = (name || '').trim();
  if (!n) return '-';
  return n.length > max ? `${n.slice(0, max - 1)}…` : n;
};

const formatFechaOC = (f) => {
  const s = String(f || '').trim();
  if (!s) return '-';
  return s.includes('T') ? s.slice(0, 10) : s.split(' ')[0];
};

const MAX_ROWS = 500;

// Iter 294.a (Carlos): drill-down de materiales y servicios por OT.
// Dos vistas: agrupado por clasificación (familia) y detalle ítem por ítem.
export default function DetalleItemsOTModal({ ot, tipoInicial, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('detalle');
  const [tipoFiltro, setTipoFiltro] = useState('ALL');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('total_linea');
  const [sortDir, setSortDir] = useState('desc');
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const res = await api.get(`/informes/ot-pdf/${ot.numero_ot}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('PDF gerencial generado — listo para imprimir y firmar');
    } catch {
      toast.error('Error al generar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/informes/ot-excel/${ot.numero_ot}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Informe_OT_${ot.numero_ot}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Informe Excel descargado');
    } catch {
      toast.error('Error al generar el Excel');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!ot) return;
    setVista('detalle');
    setSearch('');
    setSortField('total_linea');
    setSortDir('desc');
    setTipoFiltro(tipoInicial || 'ALL');
    setLoading(true);
    setData(null);
    api.get(`/compras/items-por-ot?ot_numero=${ot.numero_ot}`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Error al cargar el detalle de ítems'))
      .finally(() => setLoading(false));
  }, [ot, tipoInicial]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'descripcion' || field === 'proveedor' ? 'asc' : 'desc');
    }
  };

  const itemsFiltrados = useMemo(() => {
    let arr = data?.items || [];
    if (tipoFiltro !== 'ALL') arr = arr.filter((i) => (i.cuenta_clasif || 'MA') === tipoFiltro);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((i) =>
        String(i.descripcion || '').toLowerCase().includes(q) ||
        String(i.proveedor || '').toLowerCase().includes(q) ||
        String(i.numero_oc || '').includes(q) ||
        String(i.familia_nombre || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
      const va = a[sortField];
      const vb = b[sortField];
      if (typeof va === 'number' || typeof vb === 'number') return ((va || 0) - (vb || 0)) * dir;
      return String(va || '').localeCompare(String(vb || '')) * dir;
    });
  }, [data, tipoFiltro, search, sortField, sortDir]);

  const gruposFiltrados = useMemo(() => {
    let arr = data?.por_clasificacion || [];
    if (tipoFiltro !== 'ALL') arr = arr.filter((g) => g.cuenta_clasif === tipoFiltro);
    return arr;
  }, [data, tipoFiltro]);

  const totalFiltrado = itemsFiltrados.reduce((s, i) => s + (i.total_linea || 0), 0);

  const SortableHead = ({ field, children, className = '' }) => (
    <TableHead
      className={`py-1 cursor-pointer select-none hover:text-foreground ${className}`}
      onClick={() => toggleSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        {sortField === field && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </TableHead>
  );

  const FiltroChip = ({ value, label, count }) => (
    <button
      type="button"
      onClick={() => setTipoFiltro(value)}
      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
        tipoFiltro === value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
      }`}
      data-testid={`filtro-tipo-${value.toLowerCase()}`}
    >
      {label}{count !== undefined ? ` · ${count}` : ''}
    </button>
  );

  const counts = useMemo(() => {
    const c = { MA: 0, SE: 0, GA: 0 };
    (data?.items || []).forEach((i) => { c[i.cuenta_clasif || 'MA'] = (c[i.cuenta_clasif || 'MA'] || 0) + 1; });
    return c;
  }, [data]);

  return (
    <Dialog open={!!ot} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto" data-testid="modal-detalle-items">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Materiales y Servicios · OT-{ot?.numero_ot}
            <button
              type="button"
              onClick={handleExportPDF}
              disabled={exportingPdf || loading}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
              data-testid="btn-exportar-pdf-ot"
            >
              <FileText className="w-3.5 h-3.5" />
              {exportingPdf ? 'Generando...' : 'PDF Gerencial'}
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exporting || loading}
              className="mr-6 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid="btn-exportar-excel-ot"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              {exporting ? 'Generando...' : 'Exportar Excel'}
            </button>
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {ot?.proyecto_codigo && <span className="font-mono">{ot.proyecto_codigo} · </span>}
            {ot?.descripcion}
          </p>
        </DialogHeader>

        {loading && <Skeleton className="h-64" />}

        {!loading && data && (
          <div className="space-y-3">
            {/* Resumen */}
            <div className="text-xs bg-slate-50 dark:bg-slate-900/50 rounded p-2 grid grid-cols-3 md:grid-cols-6 gap-2">
              <div><span className="text-muted-foreground block">Total Neto</span><strong>{formatCurrency(data.total_neto)}</strong></div>
              <div><span className="text-muted-foreground block">Materiales</span><strong className="text-blue-700">{formatCurrency(data.total_materiales)}</strong></div>
              <div><span className="text-muted-foreground block">SSTT</span><strong className="text-amber-700">{formatCurrency(data.total_sstt)}</strong></div>
              <div><span className="text-muted-foreground block">Gtos. Grales.</span><strong className="text-purple-700">{formatCurrency(data.total_gastos_generales)}</strong></div>
              <div><span className="text-muted-foreground block">Ítems</span><strong>{data.cantidad_items}</strong></div>
              <div><span className="text-muted-foreground block">OCs</span><strong>{data.cantidad_ocs}</strong></div>
            </div>

            {/* Tabs + filtros */}
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setVista('detalle')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${vista === 'detalle' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="tab-detalle"
                >
                  Detalle ítem por ítem
                </button>
                <button
                  type="button"
                  onClick={() => setVista('clasif')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${vista === 'clasif' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="tab-clasificacion"
                >
                  Por Clasificación
                </button>
              </div>
              <div className="flex gap-1.5 items-center">
                <FiltroChip value="ALL" label="Todos" count={data.cantidad_items} />
                <FiltroChip value="MA" label="Materiales" count={counts.MA} />
                <FiltroChip value="SE" label="Servicios" count={counts.SE} />
                <FiltroChip value="GA" label="Gastos" count={counts.GA} />
              </div>
            </div>

            {vista === 'detalle' && (
              <>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por material, proveedor, OC o familia..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 h-8 text-sm"
                      data-testid="input-buscar-item"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {itemsFiltrados.length} ítems · <strong className="text-foreground">{formatCurrency(totalFiltrado)}</strong>
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/50">
                      <SortableHead field="descripcion">Material / Servicio</SortableHead>
                      <SortableHead field="cantidad" className="text-right">Cant.</SortableHead>
                      <TableHead className="py-1">Un.</TableHead>
                      <SortableHead field="valor_unitario" className="text-right">V. Unit</SortableHead>
                      <SortableHead field="total_linea" className="text-right">Total</SortableHead>
                      <SortableHead field="numero_oc">OC</SortableHead>
                      <SortableHead field="fecha_oc">Fecha OC</SortableHead>
                      <SortableHead field="proveedor">Proveedor</SortableHead>
                      <SortableHead field="cuenta_clasif">Tipo</SortableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemsFiltrados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-3">
                          Sin ítems para el filtro seleccionado
                        </TableCell>
                      </TableRow>
                    ) : itemsFiltrados.slice(0, MAX_ROWS).map((it, i) => {
                      const tipo = TIPOS[it.cuenta_clasif] || TIPOS.MA;
                      return (
                        <TableRow key={`it-${it.numero_oc}-${i}`} className="text-xs">
                          <TableCell className="max-w-[260px]">
                            <span className="line-clamp-2" title={it.descripcion}>{it.descripcion || '-'}</span>
                            {it.familia_nombre && <span className="block text-[10px] text-muted-foreground">{it.familia_nombre}</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono">{Number(it.cantidad || 0).toLocaleString('es-CL')}</TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">{it.unidad || '-'}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(it.valor_unitario)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{formatCurrency(it.total_linea)}</TableCell>
                          <TableCell className="font-mono text-blue-700">{it.numero_oc || '-'}</TableCell>
                          <TableCell className="text-[11px] whitespace-nowrap">{formatFechaOC(it.fecha_oc)}</TableCell>
                          <TableCell className="text-[11px]" title={it.proveedor}>{shortName(it.proveedor)}</TableCell>
                          <TableCell>
                            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${tipo.cls}`}>{it.cuenta_clasif || 'MA'}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {itemsFiltrados.length > MAX_ROWS && (
                  <p className="text-xs text-muted-foreground text-center">
                    Mostrando {MAX_ROWS} de {itemsFiltrados.length} ítems — usa el buscador para acotar.
                  </p>
                )}
              </>
            )}

            {vista === 'clasif' && (
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-muted/50">
                    <TableHead className="py-1">Clasificación / Familia</TableHead>
                    <TableHead className="py-1">Tipo</TableHead>
                    <TableHead className="py-1 text-right">Ítems</TableHead>
                    <TableHead className="py-1 text-right">Total</TableHead>
                    <TableHead className="py-1 text-right">% del total OT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gruposFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-3">
                        Sin clasificaciones para el filtro seleccionado
                      </TableCell>
                    </TableRow>
                  ) : gruposFiltrados.map((g, i) => {
                    const tipo = TIPOS[g.cuenta_clasif] || TIPOS.MA;
                    return (
                      <TableRow key={`gr-${g.cuenta_clasif}-${i}`} className="text-xs">
                        <TableCell className="font-medium">{g.familia}</TableCell>
                        <TableCell>
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${tipo.cls}`}>{tipo.label}</span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{g.cantidad_items}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(g.total)}</TableCell>
                        <TableCell className="text-right font-mono">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <span className="block h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(g.porcentaje, 100)}%` }} />
                            </span>
                            {g.porcentaje}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {gruposFiltrados.length > 0 && (
                    <TableRow className="text-xs font-bold bg-slate-100 dark:bg-slate-800">
                      <TableCell>TOTAL</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-mono">{gruposFiltrados.reduce((s, g) => s + g.cantidad_items, 0)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(gruposFiltrados.reduce((s, g) => s + g.total, 0))}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
